import { type ReactNode, useCallback } from "react";
import { useRegionConfig } from "src/utils/use-region-config";
import { AssetResolverContext } from "@hightouch/ad-studio-renderer";
import { resolveProxyPreferredFilename } from "src/pages/ad-studio/video-proxy";
import { resolveS3KeyIfCached } from "src/pages/ad-studio/presigned-urls";

/** Stills the resizer can shrink; a GIF would lose its animation. */
const RESIZABLE_IMAGE_KEY = /\.(png|jpe?g|webp)$/i;

/**
 * Resolve an `ad:<filename>` ref to a loadable `/agents/files` URL, or return
 * it unchanged when it isn't an `ad:` ref or the filename is unknown.
 */
export function resolveComponentAssetRef(
  ref: string,
  componentAssets: Record<string, string> | null | undefined,
  apiEndpoint: string,
  options?: {
    preferProxy?: boolean;
    /** Loads still images through the resizer at this width, as webp. */
    imageWidth?: number;
  },
): string {
  if (!ref.startsWith("ad:")) return ref;

  const sourceFilename = ref.slice(3);
  const filename = options?.preferProxy
    ? resolveProxyPreferredFilename(sourceFilename, componentAssets)
    : sourceFilename;
  const s3Key = componentAssets?.[filename];
  if (!s3Key) return ref;

  if (options?.imageWidth && RESIZABLE_IMAGE_KEY.test(s3Key)) {
    const params = new URLSearchParams({
      key: s3Key,
      width: String(options.imageWidth),
      format: "webp",
    });
    return `${apiEndpoint}/agents/files?${params.toString()}`;
  }

  // SVG is never primed (see primeComponentAssetUrls), so this misses for one
  // and falls through to the API.
  const cached = resolveS3KeyIfCached(s3Key);
  if (cached) return cached;

  // `stream=true` proxies the bytes instead of 302-redirecting to storage: raw
  // SVGs need the API to set image/svg+xml (see forcedImageContentTypeForKey),
  // and overlay scene markup is fetch()ed cross-origin, which a redirect to
  // the bucket would fail on CORS.
  const needsStream = /\.(svg|html)$/i.test(s3Key);

  const params = new URLSearchParams({ key: s3Key });
  if (needsStream) params.set("stream", "true");
  return `${apiEndpoint}/agents/files?${params.toString()}`;
}

export function AssetResolverProvider({
  componentAssets,
  preferProxy = false,
  imageWidth,
  children,
}: {
  componentAssets: Record<string, string> | null | undefined;
  preferProxy?: boolean;
  imageWidth?: number;
  children: ReactNode;
}) {
  const { apiEndpoint } = useRegionConfig();

  const resolve = useCallback(
    (ref: string) =>
      resolveComponentAssetRef(ref, componentAssets, apiEndpoint, {
        preferProxy,
        imageWidth,
      }),
    [componentAssets, apiEndpoint, preferProxy, imageWidth],
  );

  return (
    <AssetResolverContext.Provider value={resolve}>
      {children}
    </AssetResolverContext.Provider>
  );
}
